import { useMemo, useRef, useState } from 'react';
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from '../firebase';
import { computerCountry } from '../app/words';
import { newSession } from '../data/maps';
import { isShortLink, pinLabel, readPin } from '../data/pins';
import { LookupRows, useLookup, type LookedPlace } from '../ui/Lookup';
import { track } from '../data/analytics';
import { Mark } from '../ui/Mark';
import { signOut, accountWords } from './session';

/**
 * A business made on the web, with no phone (docs/where-users-are.md for the country): the same two records the phone
 * writes at sign-up (AuthService._createUserDocument and completeRoleSetup), so the phone reads them unchanged:
 * `riders/{uid}` as the business's owner, and `businesses/{uid}` with its name, where it works (only as the owner
 * confirms it: the computer's country is offered, never saved unasked) and where its routes start.
 */

// Every country's two-letter code (ISO 3166-1); their names come from the browser, in English.
const CODES =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ');

const zones = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
};

export function CreateBusiness({ user }: { user: User }) {
  const names = useMemo(() => {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return CODES.map((c) => ({ c, n: dn.of(c) ?? c })).sort((a, b) => a.n.localeCompare(b.n));
  }, []);
  const allZones = useMemo(zones, []);
  const here = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [owner, setOwner] = useState(user.displayName ?? '');
  const [name, setName] = useState('');
  // Offered from this computer, chosen by the owner: nothing is saved until they press Create.
  const [country, setCountry] = useState(() => {
    const c = computerCountry();
    return c && CODES.includes(c) ? c : 'NG';
  });
  const [zone, setZone] = useState(here || 'Africa/Lagos');
  const [q, setQ] = useState('');
  /** The suggestion picked, for what was typed when it was picked. */
  const [chosen, setChosen] = useState<{ q: string; address: string; placeId?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const session = useRef(newSession());
  const text = q.trim();
  const pin = text ? readPin(text) : null;
  const short = isShortLink(q);
  const picked = !pin && chosen?.q === text ? chosen : null;
  const found = useLookup(text, !picked && text.length >= 4 && !pin && !short, country.toLowerCase(), session);
  const look = found && found !== 'looking' && found.q === text ? found : null;
  // Geocoding's one place is taken as it is, and shown; the place search's suggestions wait to be picked.
  const place = picked ?? (!pin && look?.via === 'geocode' ? look.places[0] : null);
  const take = (p: LookedPlace) => {
    setChosen({ q: text, address: p.address, placeId: p.placeId ?? p.pid });
    session.current = newSession();
  };
  const create = async () => {
    if (!name.trim()) return setError('Your business needs a name: it’s what couriers and customers see.');
    setBusy(true);
    setError(null);
    const uid = user.uid;
    const start = pin ? { label: 'Shop', address: pinLabel(pin) } : place ? { label: 'Shop', address: place.address, ...(place.placeId ? { placeId: place.placeId } : {}) } : null;
    try {
      // One write, so the owner never lands in Routr Dispatch with half a business.
      await writeBatch(db)
        .set(
          doc(db, 'businesses', uid),
          {
            ownerUid: uid,
            createdAt: serverTimestamp(),
            tier: 'free',
            region: null,
            confidenceScore: null,
            name: name.trim(),
            where: { country, timeZone: zone, source: 'chosen', confirmed: true, setAt: serverTimestamp() },
            ...(start ? { places: [start] } : {}),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        )
        .set(
          doc(db, 'riders', uid),
          {
            uid,
            businessId: uid,
            name: owner.trim(),
            email: user.email ?? '',
            phone: user.phoneNumber ?? '',
            status: 'active',
            role: 'businessOwner',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        )
        .commit();
      track('business_created', { via: 'web', with_start: !!start, start_pin: !!pin, country });
    } catch (e) {
      console.warn(e);
      setError('Couldn’t create it. Check your connection and try again.');
      setBusy(false);
    }
  };
  return (
    <div className="gate">
      <div className="gate-card" style={{ width: 'min(480px, 100%)' }}>
        <div className="gate-brand">
          <Mark className="gate-mark" />
          <span className="title">Routr Dispatch</span>
        </div>
        <div className="title" style={{ fontSize: 22, marginTop: 18 }}>
          Create your business
        </div>
        <p className="cap" style={{ marginTop: 6 }}>
          Signed in as {accountWords(user)}. Your couriers join it with an invite; the Routr app is for what they do on the road.
        </p>
        <div className="gate-form" style={{ marginTop: 14 }}>
          <input className="cellin boxed" placeholder="Your name" value={owner} onChange={(e) => setOwner(e.target.value)} autoComplete="name" aria-label="Your name" />
          <input className="cellin boxed" placeholder="Your business’s name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" aria-label="Your business’s name" autoFocus />
          <label className="cap">
            Where it works
            <select className="dateinput" style={{ width: '100%', marginTop: 4 }} value={country} onChange={(e) => setCountry(e.target.value)} aria-label="The country your business works in">
              {names.map((x) => (
                <option key={x.c} value={x.c}>
                  {x.n}
                </option>
              ))}
            </select>
          </label>
          <label className="cap">
            Its time zone (its day runs midnight to midnight here)
            <select className="dateinput" style={{ width: '100%', marginTop: 4 }} value={zone} onChange={(e) => setZone(e.target.value)} aria-label="Your business’s time zone">
              {(allZones.length ? allZones : [zone]).map((z) => (
                <option key={z} value={z}>
                  {z.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </label>
          <label className="cap">
            Where routes start (optional: your shop or warehouse)
            <div className="row" style={{ marginTop: 4 }}>
              <input
                className="cellin boxed grow"
                placeholder="Its address, a pin or a Maps link"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (!place && look?.places[0]) take(look.places[0]);
                }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                aria-label="Where routes start"
              />
            </div>
            {pin && <span className="cap">The pin · {pinLabel(pin)}</span>}
            {short && !pin && <span className="cap">That’s a short Maps link, which only opens in a browser: open it, then paste the full address bar or the coordinates here.</span>}
            {found === 'looking' && <span className="cap">Looking up “{text}”…</span>}
            {place && <span className="cap">Found: {place.address}</span>}
            {look && !place && !look.places.length && <span className="cap">{look.error ?? `Couldn’t find “${text}”: try the street and the area, or paste a Maps link. You can also set it later.`}</span>}
          </label>
          {look && !place && look.places.length > 0 && (
            <div className="rows">
              <LookupRows text={text} found={look} onPick={take} />
            </div>
          )}
        </div>
        {error && (
          <div className="gate-error" role="alert">
            {error}
          </div>
        )}
        <button className="btn primary big" style={{ width: '100%', marginTop: 16 }} disabled={busy} onClick={() => void create()}>
          Create the business
        </button>
        <p className="cap" style={{ marginTop: 12, textAlign: 'center' }}>
          Not yours to create?{' '}
          <button className="link" onClick={() => void signOut()}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
